import { type FC, memo, useId, useState } from "react";
import { Button, ButtonKind, ButtonSize, ButtonVariant, Heading } from "@stubhub/broadway-web";
import { ChevronDownIcon } from "@stubhub/broadway-web/icons/functional";
import type { EventListGridItem } from "src/http/api-types";
import { useContent } from "src/shared/components/content/hooks";
import { ResidencyEventItem } from "src/website-apps/responsive/stubhub-performer/components/calendar/residency/ResidencyEventItem";
import {
  cellRecipe,
  chevronRecipe,
  dateNumberBoxRecipe,
  dateNumberRowStyle,
  disclosureInnerStyle,
  disclosureRootStyle,
  disclosureWrapperStyle,
  eventListRecipe,
} from "./PerformerDateCard.styles";

interface Props {
  dayOfMonth: number | null;
  events: EventListGridItem[];
  today: boolean;
  onClick: (event: EventListGridItem) => void;
  onMouseEnter: (event: EventListGridItem) => void;
  calendarIndex: number;
  openNotificationHandler: (eventId: number) => void;
}

export const PerformerDateCard: FC<Props> = memo(
  ({ dayOfMonth, events, today, onClick, onMouseEnter, calendarIndex, openNotificationHandler }) => {
    const [expanded, setExpanded] = useState(false);
    const headingId = useId();
    const eventListId = useId();
    const seeMoreContent = useContent("cid$#3337656#"); // See more
    const seeLessContent = useContent("cid$#4247148#"); // See less

    if (dayOfMonth === null) {
      return <div role="presentation" aria-hidden className={cellRecipe({ kind: "empty" })} />;
    }

    if (events.length === 0) {
      return (
        <div role="gridcell" aria-labelledby={headingId} className={cellRecipe({ kind: "empty" })}>
          <div className={dateNumberBoxRecipe({ todayHighlight: today ? "today" : "default" })}>
            <Heading id={headingId} variant="title" size="small" as="h3">
              {dayOfMonth}
            </Heading>
          </div>
        </div>
      );
    }

    const MAX_VISIBLE = 2;
    const visibleEvents = expanded ? events : events.slice(0, MAX_VISIBLE);
    const hiddenCount = events.length - MAX_VISIBLE;
    const hasMore = !expanded && hiddenCount > 0;

    return (
      <div
        role="gridcell"
        className={cellRecipe({ kind: "withEvents", expanded: expanded ? "expanded" : "collapsed" })}
      >
        <div className={dateNumberRowStyle}>
          <div className={dateNumberBoxRecipe({ todayHighlight: today ? "today" : "default" })}>
            <Heading variant="title" size="small" as="h3">
              {dayOfMonth}
            </Heading>
          </div>
        </div>
        <div
          id={eventListId}
          className={eventListRecipe({
            count: events.length === 1 ? "single" : "multiple",
            disclosure: expanded ? "expanded" : "collapsed",
          })}
        >
          {visibleEvents.map((event) => (
            <ResidencyEventItem
              key={event.eventId}
              event={event}
              calendarIndex={calendarIndex}
              onClick={onClick}
              onMouseEnter={onMouseEnter}
              openNotificationHandler={openNotificationHandler}
            />
          ))}
          {hasMore && (
            <Button
              aria-controls={eventListId}
              aria-expanded={false}
              classes={{ root: disclosureRootStyle }}
              kind={ButtonKind.Neutral}
              onClick={() => setExpanded(true)}
              size={ButtonSize.Small}
              variant={ButtonVariant.Ghost}
            >
              <span className={disclosureInnerStyle}>
                {seeMoreContent}
                <span className={chevronRecipe({ rotated: "default" })}>
                  <ChevronDownIcon size={16} />
                </span>
              </span>
            </Button>
          )}
        </div>
        {expanded && events.length > MAX_VISIBLE && (
          <div className={disclosureWrapperStyle}>
            <Button
              aria-controls={eventListId}
              aria-expanded
              classes={{ root: disclosureRootStyle }}
              kind={ButtonKind.Neutral}
              onClick={() => setExpanded(false)}
              size={ButtonSize.Small}
              variant={ButtonVariant.Ghost}
            >
              <span className={disclosureInnerStyle}>
                {seeLessContent}
                <span className={chevronRecipe({ rotated: "rotated" })}>
                  <ChevronDownIcon size={16} />
                </span>
              </span>
            </Button>
          </div>
        )}
      </div>
    );
  },
);

PerformerDateCard.displayName = "PerformerDateCard";
